/*
 * Proprietary and confidential | 2023 | SoundTalks NV
 */

import React from 'react';
import { Link } from 'react-router';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { withTranslation, Trans } from 'react-i18next';
import { LightInput, PrimaryButton, FieldWrapper, GeneralFormError } from 'ui';
import { axios, ImmutablePropTypes } from 'data';
import { transformCountryOptions } from 'utils/format-data';
import { termsConditionsPath, dataPrivacyPath, signInPath } from 'constants/urls';
import { isChinaConfig } from 'config';
import { Form, Field } from 'formik';
import colors from 'ui/styles/colors.module.scss';
import s from './styles.module.scss';
import sharedStyles from '../../styles.module.scss';
import { LightSelect } from 'components/core/select';
import PhoneInput from 'components/core/phone-input';
import Checkbox from 'components/core/checkbox';
import Icon from 'components/core/icon';
import HeaderShared from 'components/shared/layout/header';

// Merge local and shared styles
const styles = { ...s, ...sharedStyles };

const pickerValueAdapter = (value) => value.value;

const getTimezones = () =>
  axios.get('/timezones-offset.json').then((res) => {
    const { data } = res;
    return data.map((zone) => ({
      value: zone.value,
      label: zone.label,
    }));
  });

class RegisterForm extends React.Component {
  state = {
    timezones: [],
  };

  componentDidMount() {
    getTimezones().then((timezones) => {
      this.setState({
        timezones,
      });
    });
  }

  handleOnChange = (value) => {
    this.props.setFieldValue('telephone', value);
  };

  onCountryChange = (option) => {
    this.props.handleSelectCountry(option);
    this.props.setFieldValue('country', option.value);
  };

  render() {
    const {
      className,
      countries,
      values,
      errors,
      validateForm,
      setFieldValue,
      t,
      isWeChatRegistration,
      ...formikProps
    } = this.props;
    const countryOptions = transformCountryOptions(countries);

    return (
      <div className={cx(styles.sharedForm, isWeChatRegistration && styles.step1WithWeChat, className)}>
        <HeaderShared>
          <Icon className={styles.sharedFormLogo} icon={Icon.icons.logo} />
        </HeaderShared>
        <HeaderShared className={styles.sharedFormFooterRegistration} style={{ color: colors.secondary }}>
          {t('already_have_account')}
          <Link to={signInPath} className={styles.sharedFormFooterLink}>
            {t('sign_in')}
          </Link>
        </HeaderShared>

        {isWeChatRegistration && (
          <div className={styles.sharedFormWeChatMessage}>
            <p>{t('wechat_register_new_account')}</p>
          </div>
        )}

        <Form>
          <Field
            id="first_name"
            name="first_name"
            placeholder={t('first_name')}
            component={FieldWrapper}
            inputComponent={LightInput}
            className={styles.sharedFormField}
            // eslint-disable-next-line camelcase
            error={errors.first_name}
          />
          <Field
            id="last_name"
            name="last_name"
            placeholder={t('last_name')}
            component={FieldWrapper}
            inputComponent={LightInput}
            className={styles.sharedFormField}
            // eslint-disable-next-line camelcase
            error={errors.last_name}
          />
          <Field
            id="email"
            name="email"
            placeholder={isWeChatRegistration ? `${t('email')} (${t('optional')})` : t('email')}
            component={FieldWrapper}
            inputComponent={LightInput}
            className={styles.sharedFormField}
            error={errors.email || errors.description}
          />
          {!isChinaConfig && (
            <Field
              id="telephone"
              name="telephone"
              placeholder={t('telephone')}
              inputComponent={PhoneInput}
              options={this.props.countries.map((country) => ({ ...country, value: country.phone_code }))}
              component={FieldWrapper}
              className={styles.sharedFormField}
              handleOnChange={this.handleOnChange}
              errorColor={colors.error}
              error={errors.telephone}
            />
          )}
          {!isWeChatRegistration && (
            <>
              <Field
                id="password"
                name="password"
                secureTextEntry
                placeholder={t('password')}
                component={FieldWrapper}
                inputComponent={LightInput}
                className={styles.sharedFormField}
                error={errors.password || errors.password?.join(' ')}
                type="password"
              />
              <Field
                id="confirm_password"
                name="confirm_password"
                secureTextEntry
                placeholder={t('confirm_password')}
                component={FieldWrapper}
                inputComponent={LightInput}
                className={styles.sharedFormField}
                type="password"
              />
            </>
          )}
          <Field
            id="language"
            name="language"
            placeholder={t('select_language')}
            inputComponent={LightSelect}
            onChangeAdapter={pickerValueAdapter}
            options={this.props.languages?.toJS()}
            component={FieldWrapper}
            valueAdapter={(value) => this.props.languages?.toJS().find((x) => x.value === value)}
            className={styles.sharedFormField}
            error={errors.language}
          />
          {!isChinaConfig && (
            <Field
              id="country"
              name="country"
              placeholder={t('select_country')}
              inputComponent={LightSelect}
              options={countryOptions}
              component={FieldWrapper}
              valueAdapter={(value) => countryOptions.find((x) => x.value === value)}
              className={styles.sharedFormField}
              onChange={this.onCountryChange}
              error={errors.country}
            />
          )}
          {!isChinaConfig && (
            <Field
              id="timezone"
              name="timezone"
              placeholder={t('select_timezone')}
              inputComponent={LightSelect}
              onChangeAdapter={pickerValueAdapter}
              options={this.state.timezones}
              component={FieldWrapper}
              valueAdapter={(value) => this.state.timezones.find((x) => x.value === value)}
              className={styles.sharedFormField}
              error={errors.timezone}
            />
          )}

          <Field
            component={Checkbox}
            checked={values.policy_accepted}
            className={styles.sharedFormField}
            id="policy_accepted_checkbox"
            name="policy_accepted_checkbox"
            label={
              <div className={styles.sharedFormPolicyLabel}>
                <Trans i18nKey="agree_to_terms_of_use_and_data_privacy">
                  <Link to={termsConditionsPath} className={styles.sharedFormPolicyLink} />
                  <Link to={dataPrivacyPath} className={styles.sharedFormPolicyLink} />
                </Trans>
              </div>
            }
            onChange={() => setFieldValue('policy_accepted', !values.policy_accepted)}
            dark
          />

          <PrimaryButton
            type="button"
            className={styles.sharedFormSubmit}
            onClick={() => validateForm(values, formikProps)}
            isDisabled={!values.policy_accepted || !formikProps.isValid}
          >
            {t('next')}
          </PrimaryButton>

          <GeneralFormError error={errors.description || errors[0]} />
        </Form>
      </div>
    );
  }
}

RegisterForm.defaultProps = {
  className: null,
  values: null,
  errors: null,
  languages: null,
};

RegisterForm.propTypes = {
  className: PropTypes.string,
  errors: PropTypes.shape(),
  values: PropTypes.shape(),
  validateForm: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  handleSelectCountry: PropTypes.func.isRequired,
  languages: ImmutablePropTypes.list,
  t: PropTypes.func.isRequired,
  isWeChatRegistration: PropTypes.bool,
};

export default withTranslation()(RegisterForm);
